import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import useIsAuthenticate from "./hooks/useIsAuthenticate";
import { useLmcUserView } from "../../../contexts/AuthContext";
import { useForm } from "react-hook-form";
import SocialAuth from "./SocialAuth";
import { IResolveParams } from "reactjs-social-login";
import { CustomInput } from "../../atoms/CustomInput";
import { CustomButton } from "../../atoms/CustomButton";
import {
  useUserForgotPassword,
  useUserSocialForgotPassword,
} from "../../../hooks/users";
import { lmcInputClass, lmcFieldClass } from "../../../utils/utils";
import { Timer } from "../../../utils/Timer";
import { usePasswordResetToasts } from "../../../hooks/usePasswordResetToasts";

const LmcUserViewForgotPassword = () => {
  const { t } = useTranslation("clientView");
  const navigate = useNavigate();
  const { processId } = useParams();
  const isAuthenticated = useIsAuthenticate();

  const {
    state: {
      entity: { entity_data },
    },
  } = useLmcUserView();

  const [showTimer, setShowTimer] = useState(false);
  const { mutate: forgetPassword, isLoading: isForgeting } =
    useUserForgotPassword({ notify: false });
  const { mutate: socialForgetPassword, isLoading: isSocialForgeting } =
    useUserSocialForgotPassword({ notify: false });
  const resetToasts = usePasswordResetToasts();

  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm();

  const onSubmit = (data: any) => {
    forgetPassword(
      {
        entityIdentification: entity_data?.entity_identification,
        user_email: data.email_address,
      },
      {
        onSuccess: () => {
          setShowTimer(true);
          resetToasts.sent();
        },
        onError: (err) => {
          setShowTimer(false);
          resetToasts.refused(err);
        },
      }
    );
  };

  const handleLoginStartSocialAuth = () => {};

  const handleResolveSocialAuth = (
    provider: IResolveParams["provider"],
    data: IResolveParams["data"]
  ) => {
    try {
      const cpPayload = {
        access_token:
          provider === "google"
            ? data?.access_token
            : data?.authorization?.id_token,
        provider,
      };

      socialForgetPassword(
        {
          data: { providerPayload: cpPayload },
        },
        {
          onSuccess({ data }, variables, context) {
            onSubmit({
              email_address: data.element.user_email,
            });
          },
          onError: resetToasts.refused,
        }
      );
    } catch (ex) {
      console.log(ex);
    }
  };

  const handleRejectSocialAuth = (err: any) => {
    console.log(err);
  };

  useEffect(() => {
    if (isAuthenticated && processId)
      navigate(`/loyalty-program/${processId}/dashboard`, { replace: true });
  }, [isAuthenticated, navigate, processId]);

  return (
    <main className="w-full p-4 gap-4">
      <div className="w-full flex-col justify-between items-center flex">
        <SocialAuth
          className="w-full"
          providers={["Google", "Apple"]}
          onLoginStartSocialAuthStart={handleLoginStartSocialAuth}
          onResolveSocialAuth={handleResolveSocialAuth}
          onRejectSocialAuth={handleRejectSocialAuth}
          redirectUrl={{
            Apple: `${process.env.REACT_APP_APPLE_CALLBACK}`,
          }}
          providersIcon={{
            Apple: (
              <div className="bg-gray-700 rounded-full px-2.5 w-full h-11 flex flex-row justify-center items-center cursor-pointer relative mb-4">
                <img
                  src="../../images/apple.svg"
                  alt=""
                  className="w-6 h-6 absolute top-2.5 start-2.5"
                />
                <p className="text-gray-50 text-base font-semibold font-figtree leading-normal">
                  {t("key18")}
                </p>
              </div>
            ),
            Google: (
              <div className="bg-gray-700 rounded-full px-2.5 w-full h-11 flex flex-row justify-center items-center cursor-pointer relative">
                <img
                  src="../../images/google.svg"
                  alt=""
                  className="w-6 h-6 absolute top-2.5 start-2.5"
                />
                <p className="text-gray-50 text-base font-semibold font-figtree leading-normal">
                  {t("key19")}
                </p>
              </div>
            ),
          }}
        />
      </div>

      <div className="flex items-center gap-4">
        <div className="flex-1 h-px bg-white/20"></div>
        <p className="text-center text-gray-400 text-xs font-medium font-figtree uppercase leading-none">
          {t("key20")}
        </p>
        <div className="flex-1 h-px bg-white/20"></div>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="w-full h-[calc(30lvh-30px)] flex flex-col relative"
      >
        <div className="w-full">
          <CustomInput
            name="email_address"
            control={control}
            error={errors.email_address}
            helperText={
              errors.email_address?.type === "required"
                ? (t("key22") as string)
                : errors.email_address?.type === "pattern"
                ? (t("key23") as string)
                : ""
            }
            grayBackground
            type="email"
            className={lmcInputClass}
            inputClassName={lmcFieldClass}
            label={t("key21") as string}
            rules={{
              required: true,
              pattern: {
                value: /[^@ \t\r\n]+@[^@ \t\r\n]+\.[^@ \t\r\n]+/,
                message: t("key23"),
              },
            }}
            inputDir="ltr"
          />
        </div>

        <div className="w-full max-w-[400px] px-4 mx-auto mt-auto bottom-5 left-0 right-0">
          <CustomButton
            variant="primary"
            className="w-full"
            type="submit"
            disabled={showTimer}
            isLoading={isForgeting || isSocialForgeting}
            comp={
              showTimer && (
                <span className="text-muted ms-2">
                  <Timer setShowTimer={setShowTimer} ttl={120} />{" "}
                  {t("register.confirmEmailModal.text_5")}
                </span>
              )
            }
            title={t("register.confirmEmailModal.btn_2") as string}
          />
        </div>
      </form>
    </main>
  );
};

export default LmcUserViewForgotPassword;
